import { useState } from 'react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Checkbox } from '@/components/ui/checkbox'
import { Label } from '@/components/ui/label'
import { Button } from '@/components/ui/button'
import { Plus, ChevronLeft, ChevronRight, Pill } from 'lucide-react'
import { MedicamentoForm } from './MedicamentoForm'
import { MedicamentoItem } from './MedicamentoItem'
import type { Medication } from '@/types/medicamento'

interface Etapa4Props {
  formData: any
  onChange: (field: string, value: any) => void
  onNext: () => void
  onPrev: () => void
  onSubmit: () => void
  saving: boolean
  submitting: boolean
}

export function Etapa4Medicamentos({ formData, onChange, onNext, onPrev }: Etapa4Props) {
  const medicamentos: Medication[] = formData.medicamentos || []
  const [showForm, setShowForm] = useState(false)
  const [editingIndex, setEditingIndex] = useState<number | null>(null)

  const validateAndProceed = () => {
    if (!formData.veracidade_etapa4) {
      alert('Por favor, confirme a declaração de veracidade.')
      return
    }
    onNext()
  }

  const handleSave = (med: Medication) => {
    if (editingIndex !== null) {
      const updated = medicamentos.map((m, i) => i === editingIndex ? med : m)
      onChange('medicamentos', updated)
      setEditingIndex(null)
    } else {
      onChange('medicamentos', [...medicamentos, med])
    }
    setShowForm(false)
  }

  const handleEdit = (med: Medication) => {
    const idx = medicamentos.indexOf(med)
    setEditingIndex(idx)
    setShowForm(true)
  }

  const handleRemove = (index: number) => {
    if (!confirm('Remover este medicamento?')) return
    onChange('medicamentos', medicamentos.filter((_, i) => i !== index))
  }

  const handleCancel = () => {
    setShowForm(false)
    setEditingIndex(null)
  }

  const editingMed = editingIndex !== null ? medicamentos[editingIndex] : undefined

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Pill className="h-5 w-5" />
            Medicamentos em Uso
          </CardTitle>
          <p className="text-sm text-gray-600">
            Liste todos os medicamentos que o idoso(a) utiliza atualmente, com o máximo de detalhes possível.
          </p>
        </CardHeader>
        <CardContent className="space-y-4">
          {medicamentos.length === 0 && !showForm && (
            <div className="text-center py-8 border-2 border-dashed border-gray-300 rounded-lg">
              <Pill className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-500">Nenhum medicamento adicionado</p>
              <Button onClick={() => setShowForm(true)} className="mt-4">
                <Plus className="h-4 w-4 mr-2" />
                Adicionar Medicamento
              </Button>
            </div>
          )}

          {medicamentos.length > 0 && !showForm && (
            <div className="space-y-2">
              {medicamentos.map((med, index) => (
                <MedicamentoItem
                  key={index}
                  medicamento={med}
                  index={index}
                  onEdit={handleEdit}
                  onRemove={handleRemove}
                />
              ))}
            </div>
          )}

          {showForm && (
            <MedicamentoForm
              initialData={editingMed}
              onSave={handleSave}
              onCancel={handleCancel}
            />
          )}

          {!showForm && medicamentos.length > 0 && (
            <Button onClick={() => setShowForm(true)} variant="outline" className="w-full">
              <Plus className="h-4 w-4 mr-2" />
              Adicionar Outro Medicamento
            </Button>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Termo de Veracidade</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <p className="text-sm text-gray-700">
              Declaro que as informações prestadas sobre os medicamentos são verdadeiras e
              foram fornecidas com base no meu conhecimento sobre o tratamento do idoso(a).
            </p>
            <div className="flex items-center space-x-2">
              <Checkbox
                id="veracidade_etapa4"
                checked={formData.veracidade_etapa4 || false}
                onCheckedChange={(checked) => onChange('veracidade_etapa4', checked as boolean)}
                className="!rounded-full data-[state=checked]:!bg-green-600 data-[state=checked]:!border-green-600"
              />
              <Label htmlFor="veracidade_etapa4" className="text-sm font-medium">
                Declaro que as informações são verdadeiras *
              </Label>
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="flex justify-between">
        <Button variant="outline" onClick={onPrev}>
          <ChevronLeft className="h-4 w-4 mr-2" />
          Anterior
        </Button>
        <Button onClick={validateAndProceed}>
          Próxima Etapa
          <ChevronRight className="h-4 w-4 ml-2" />
        </Button>
      </div>
    </div>
  )
}
